import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AdminTopbar from "../components/AdminTopbar";
import API from "../services/api";
import { getStoredAdmin, isAdminSessionValid } from "../utils/adminAuth";

function AdminLayout({ children }) {
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(() => isAdminSessionValid());
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = viewportWidth <= 900;
  const isLaptop = viewportWidth > 900 && viewportWidth <= 1366;

  useEffect(() => {
    document.documentElement.dataset.adminShell = "1";
    return () => {
      delete document.documentElement.dataset.adminShell;
    };
  }, []);

  useEffect(() => {
    if (!isAllowed) {
      navigate("/admin-login", { replace: true });
    }
  }, [isAllowed, navigate]);

  useEffect(() => {
    if (!isAdminSessionValid()) {
      setIsAllowed(false);
      return undefined;
    }

    let active = true;
    API.get("/admin/session")
      .then((res) => {
        if (!active) return;
        const current = getStoredAdmin();
        const merged = {
          ...(current || {}),
          ...(res.data || {}),
          loginAt: current?.loginAt || Date.now(),
        };
        localStorage.setItem("admin", JSON.stringify(merged));
        setIsAllowed(true);
      })
      .catch(() => {
        localStorage.removeItem("admin");
        if (active) setIsAllowed(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;
      const mobile = width <= 900;
      setViewportWidth(width);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!isAllowed) return null;

  return (
    <div style={shell} className="admin-shell">
      <AdminSidebar isMobile={isMobile} isLaptop={isLaptop} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {isMobile && sidebarOpen && <button type="button" aria-label="Close admin menu" style={overlay} onClick={() => setSidebarOpen(false)} />}
      <main style={isMobile ? mobileMain : isLaptop ? laptopMain : main} className="admin-main">
        <AdminTopbar isMobile={isMobile} isLaptop={isLaptop} onMenuClick={() => setSidebarOpen(true)} />
        <div style={isMobile ? mobileContent : isLaptop ? laptopContent : content} className="admin-content">{children}</div>
      </main>
      <button type="button" style={isMobile ? mobileRefreshButton : refreshButton} onClick={() => window.location.reload()} title="Refresh current admin page">
        <RefreshCw size={17} />
      </button>
      <style>{responsiveCss}</style>
    </div>
  );
}

const shell = {
  minHeight: "100vh",
  height: "100dvh",
  background: "#f8fafc",
  overflowX: "hidden",
  overflowY: "hidden"
};

const main = {
  marginLeft: "320px",
  minHeight: "100vh",
  height: "100dvh",
  transition: "margin-left 0.25s ease",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
};

const laptopMain = {
  ...main,
  marginLeft: "260px"
};

const mobileMain = {
  marginLeft: 0,
  minHeight: "100vh",
  height: "100dvh",
  width: "100%",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  overflowY: "hidden"
};

const content = {
  padding: "20px 22px 38px",
  background: "#f8fafc",
  minHeight: "calc(100vh - 84px)",
  boxSizing: "border-box",
  overflowX: "hidden",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorY: "contain",
  flex: "1 1 auto"
};

const laptopContent = {
  ...content,
  padding: "14px 14px 34px",
  minHeight: "calc(100vh - 72px)"
};

const mobileContent = {
  ...content,
  padding: "calc(8px + env(safe-area-inset-top)) 8px calc(84px + env(safe-area-inset-bottom))",
  minHeight: "calc(100vh - 56px)",
  overflowX: "hidden",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorY: "contain",
  paddingTop: "calc(8px + env(safe-area-inset-top))"
};

const overlay = {
  position: "fixed",
  inset: 0,
  zIndex: 998,
  border: 0,
  background: "rgba(15,23,42,0.45)"
};

const refreshButton = {
  position: "fixed",
  right: "18px",
  bottom: "18px",
  zIndex: 900,
  width: "44px",
  height: "44px",
  border: "1px solid #bfdbfe",
  borderRadius: "999px",
  background: "linear-gradient(135deg,#eff6ff,#ffffff)",
  color: "#2563eb",
  display: "grid",
  placeItems: "center",
  boxShadow: "0 14px 34px rgba(37,99,235,.18)",
  cursor: "pointer"
};

const mobileRefreshButton = {
  ...refreshButton,
  right: "12px",
  bottom: "calc(74px + env(safe-area-inset-bottom))",
  width: "42px",
  height: "42px"
};

const responsiveCss = `
  :root {
    --admin-ui-zoom: 1;
  }
  html[data-admin-shell="1"],
  html[data-admin-shell="1"] body,
  html[data-admin-shell="1"] #root {
    background: #f8fafc !important;
    min-height: 100dvh !important;
  }
  .admin-shell {
    zoom: var(--admin-ui-zoom, 1);
    min-height: calc(100dvh / var(--admin-ui-zoom, 1)) !important;
    height: calc(100dvh / var(--admin-ui-zoom, 1)) !important;
    background: #f8fafc !important;
  }
  .admin-main {
    background: inherit;
  }
  [data-admin-theme="dark"] body {
    background: #050a14 !important;
  }
  [data-admin-theme="dark"] .admin-shell,
  [data-admin-theme="dark"] .admin-main {
    background:
      linear-gradient(180deg, #07111f 0%, #050a14 48%, #08111d 100%) !important;
    color: #e2e8f0 !important;
  }
  [data-admin-theme="dark"] .admin-sidebar,
  [data-admin-theme="dark"] .admin-topbar {
    background: #07111f !important;
    border-color: #1e2e45 !important;
    color: #e2e8f0 !important;
  }
  [data-admin-theme="dark"] .admin-content {
    background:
      linear-gradient(180deg, #07111f 0%, #050a14 52%, #08111d 100%) !important;
    color: #e2e8f0 !important;
  }
  [data-admin-theme="dark"] .admin-content h1,
  [data-admin-theme="dark"] .admin-content h2,
  [data-admin-theme="dark"] .admin-content h3 {
    color: #f8fafc !important;
  }
  [data-admin-theme="dark"] .admin-content [style*="color: #0f172a"],
  [data-admin-theme="dark"] .admin-content [style*="color:#0f172a"],
  [data-admin-theme="dark"] .admin-content [style*="color: rgb(15, 23, 42)"] {
    color: #f8fafc !important;
  }
  [data-admin-theme="dark"] .admin-content [style*="color: #334155"],
  [data-admin-theme="dark"] .admin-content [style*="color:#334155"],
  [data-admin-theme="dark"] .admin-content [style*="color: #475569"],
  [data-admin-theme="dark"] .admin-content [style*="color:#475569"],
  [data-admin-theme="dark"] .admin-content [style*="color: #64748b"],
  [data-admin-theme="dark"] .admin-content [style*="color:#64748b"],
  [data-admin-theme="dark"] .admin-content [style*="color: #94a3b8"],
  [data-admin-theme="dark"] .admin-content [style*="color:#94a3b8"] {
    color: #9fb0c7 !important;
  }
  [data-admin-theme="dark"] .admin-content p,
  [data-admin-theme="dark"] .admin-content small,
  [data-admin-theme="dark"] .admin-content span,
  [data-admin-theme="dark"] .admin-content label {
    border-color: #26354d;
  }
  [data-admin-theme="dark"] .admin-content section,
  [data-admin-theme="dark"] .admin-content article,
  [data-admin-theme="dark"] .admin-content form,
  [data-admin-theme="dark"] .admin-content table,
  [data-admin-theme="dark"] .admin-content .admin-modal-card {
    background: #0b1424 !important;
    border-color: #26354d !important;
    color: #e2e8f0 !important;
    box-shadow: 0 18px 45px rgba(0,0,0,.28) !important;
  }
  [data-admin-theme="dark"] .admin-content header,
  [data-admin-theme="dark"] .admin-content [style*="background: #fff"],
  [data-admin-theme="dark"] .admin-content [style*="background: rgb(255, 255, 255)"],
  [data-admin-theme="dark"] .admin-content [style*="background:#fff"] {
    background: #0f1a2c !important;
    border-color: #26354d !important;
    color: #e2e8f0 !important;
  }
  [data-admin-theme="dark"] .admin-sidebar input,
  [data-admin-theme="dark"] .admin-content input,
  [data-admin-theme="dark"] .admin-content select,
  [data-admin-theme="dark"] .admin-content textarea {
    background: #07111f !important;
    border-color: #31445f !important;
    color: #f8fafc !important;
    box-shadow: none !important;
  }
  [data-admin-theme="dark"] .admin-content input::placeholder,
  [data-admin-theme="dark"] .admin-content textarea::placeholder {
    color: #7d8da7 !important;
  }
  [data-admin-theme="dark"] .admin-sidebar button,
  [data-admin-theme="dark"] .admin-topbar button,
  [data-admin-theme="dark"] .admin-content button {
    border-color: #31445f;
  }
  [data-admin-theme="dark"] .admin-sidebar button,
  [data-admin-theme="dark"] .admin-topbar button {
    background: #0f1a2c !important;
    color: #e2e8f0 !important;
  }
  [data-admin-theme="dark"] .admin-content th,
  [data-admin-theme="dark"] .admin-content td {
    color: #dbeafe !important;
    border-color: #26354d !important;
  }
  [data-admin-theme="dark"] .admin-content tr {
    background: #0f1a2c !important;
    border-color: #26354d !important;
  }
  [data-admin-theme="dark"] .admin-content a {
    color: #93c5fd !important;
  }
  .admin-content * { box-sizing: border-box; }
  .admin-content img, .admin-content video, .admin-content canvas { max-width: 100%; }
  .admin-content > * {
    min-width: 0;
  }
  .admin-content div,
  .admin-content section,
  .admin-content article,
  .admin-content header,
  .admin-content form,
  .admin-content label {
    min-width: 0;
  }
  .admin-content table { width: 100%; border-collapse: collapse; }
  @media (min-width: 901px) and (max-width: 1366px) {
    .admin-content {
      font-size: 13px;
    }
    .admin-content h1 { font-size: 23px !important; }
    .admin-content h2 { font-size: 19px !important; }
    .admin-content [style*="padding: 28px"],
    .admin-content [style*="padding: 24px"],
    .admin-content [style*="padding: 22px"],
    .admin-content [style*="padding: 20px"] {
      padding: 16px !important;
    }
    .admin-content [style*="gap: 24px"],
    .admin-content [style*="gap: 22px"],
    .admin-content [style*="gap: 20px"] {
      gap: 14px !important;
    }
    .admin-content [style*="repeat(7"] {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(6"],
    .admin-content [style*="repeat(5"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(4"] {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-dashboard-kpis {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 12px !important;
    }
    .admin-content .admin-dashboard-minis {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 10px !important;
    }
    .admin-content .admin-outcome-form {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 12px !important;
    }
    .admin-content .admin-outcome-form label:nth-child(2),
    .admin-content .admin-outcome-form label:nth-child(3) {
      grid-column: auto !important;
    }
    .admin-content table {
      font-size: 12px;
    }
    .admin-content th,
    .admin-content td {
      padding-left: 10px !important;
      padding-right: 10px !important;
    }
    .admin-content button,
    .admin-content input,
    .admin-content select,
    .admin-content textarea {
      font-size: 13px !important;
    }
  }
  @media (max-width: 900px) {
    html, body { overflow-x: hidden; }
    .admin-content {
      width: 100%;
      max-width: 100vw;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      font-size: 12px;
    }
    .admin-content h1 { font-size: 21px !important; line-height: 1.15 !important; }
    .admin-content h2 { font-size: 18px !important; line-height: 1.2 !important; }
    .admin-content h3 { font-size: 15px !important; line-height: 1.2 !important; }
    .admin-content p { font-size: 12px !important; }
    .admin-content header {
      flex-wrap: wrap !important;
      gap: 10px !important;
      align-items: stretch !important;
    }
    .admin-content header > div {
      min-width: 0 !important;
    }
    .admin-content header > div:last-child {
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
      max-width: 100% !important;
    }
    .admin-content section,
    .admin-content article,
    .admin-content form,
    .admin-content header,
    .admin-content label,
    .admin-content div {
      max-width: 100%;
      min-width: 0;
    }
    .admin-content [style*="minmax(320px"],
    .admin-content [style*="minmax(300px"],
    .admin-content [style*="minmax(280px"],
    .admin-content [style*="minmax(260px"],
    .admin-content [style*="minmax(240px"] {
      grid-template-columns: 1fr !important;
    }
    .admin-content [style*="gridTemplateColumns: minmax"],
    .admin-content [style*="grid-template-columns: minmax"] {
      grid-template-columns: 1fr !important;
    }
    .admin-content [style*="1.1fr"],
    .admin-content [style*=".9fr"],
    .admin-content [style*="1fr auto auto"],
    .admin-content [style*="1.2fr 1fr auto auto"],
    .admin-content [style*="44px 1fr 124px"],
    .admin-content [style*="44px 1fr 194px"],
    .admin-content [style*="repeat(9"] {
      grid-template-columns: 1fr !important;
    }
    .admin-content [style*="display: flex"] {
      flex-wrap: wrap !important;
    }
    .admin-content button[style*="display: flex"],
    .admin-content button[style*="display: inline-flex"],
    .admin-content a[style*="display: flex"],
    .admin-content a[style*="display: inline-flex"] {
      flex-wrap: nowrap !important;
    }
    .admin-content [style*="padding: 28px"],
    .admin-content [style*="padding: 24px"],
    .admin-content [style*="padding: 22px"],
    .admin-content [style*="padding: 20px"] {
      padding: 12px !important;
    }
    .admin-content [style*="gap: 24px"],
    .admin-content [style*="gap: 22px"],
    .admin-content [style*="gap: 20px"],
    .admin-content [style*="gap: 18px"] {
      gap: 10px !important;
    }
    .admin-content [style*="repeat(6"],
    .admin-content [style*="repeat(5"],
    .admin-content [style*="repeat(4"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(3"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(2"] {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    @media (max-width: 520px) {
      .admin-content [style*="repeat(4"],
      .admin-content [style*="repeat(3"],
      .admin-content [style*="repeat(2"],
      .admin-content [style*="1fr 1fr"] {
        grid-template-columns: 1fr !important;
      }
      .admin-content .admin-dashboard-kpis,
      .admin-content .admin-dashboard-minis {
        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      }
      .admin-content .admin-outcome-form {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
    }
    .admin-content input,
    .admin-content select,
    .admin-content textarea {
      font-size: 16px !important;
      max-width: 100%;
      min-height: 40px;
    }
    .admin-content button {
      max-width: 100%;
      min-height: 38px;
      font-size: 12px !important;
    }
    .admin-content select,
    .admin-content textarea {
      -webkit-appearance: none;
      appearance: none;
    }
    .admin-content button {
      padding-left: 10px !important;
      padding-right: 10px !important;
      white-space: nowrap;
    }
    .admin-content table {
      display: block;
      width: 100% !important;
      min-width: 0 !important;
      border-collapse: separate;
      border-spacing: 0 6px;
      overflow: visible;
    }
    .admin-content thead {
      display: none;
    }
    .admin-content tbody,
    .admin-content tr,
    .admin-content td {
      display: block;
      width: 100% !important;
    }
    .admin-content tr {
      margin-bottom: 6px;
      padding: 7px 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
    }
    .admin-content td {
      border: 0 !important;
      padding: 3px 1px !important;
      font-size: 11px !important;
      white-space: normal !important;
      word-break: break-word;
      line-height: 1.2 !important;
    }
    .admin-content td * {
      max-width: 100%;
      white-space: normal !important;
      word-break: break-word;
    }
    .admin-content td [style*="display: flex"] {
      min-width: 0 !important;
    }
    .admin-content td:empty {
      display: none;
    }
    .admin-content tr td:first-child {
      font-weight: 900;
      color: #0f172a;
    }
    .admin-content tr td:last-child {
      display: block;
      padding-top: 6px !important;
    }
    .admin-content tr td:last-child > div,
    .admin-content tr td:last-child button {
      display: inline-flex !important;
      flex-wrap: wrap;
      gap: 5px !important;
      align-items: center;
    }
    .admin-content tr td:last-child input,
    .admin-content tr td:last-child select,
    .admin-content tr td:last-child textarea {
      width: 100% !important;
    }
    .admin-content .admin-mobile-full {
      grid-column: 1 / -1 !important;
    }
    .admin-content .admin-mobile-two {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-mobile-three {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-mobile-stack {
      grid-template-columns: 1fr !important;
    }
    .admin-content .admin-mobile-hide {
      display: none !important;
    }
    .admin-content .admin-mobile-actions {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-mobile-actions button {
      min-height: 40px !important;
      border-radius: 12px !important;
    }
    .admin-content .admin-starline-form-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 10px !important;
    }
    .admin-content .admin-starline-slot-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-starline-button-row {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }
    .admin-content .admin-market-header {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 10px !important;
      align-items: stretch !important;
    }
    .admin-content .admin-market-header-buttons {
      display: grid !important;
      grid-template-columns: minmax(0, 92px) 1fr !important;
      gap: 8px !important;
      width: 100% !important;
    }
    .admin-content .admin-market-refresh-button,
    .admin-content .admin-market-create-button {
      min-height: 42px !important;
      height: 42px !important;
      border-radius: 12px !important;
      justify-content: center !important;
      display: inline-flex !important;
      align-items: center !important;
      gap: 6px !important;
      white-space: nowrap !important;
    }
    .admin-content .admin-market-stats-grid,
    .admin-content .admin-market-form-grid,
    .admin-content .admin-market-time-grid,
    .admin-content .admin-market-footer-row {
      grid-template-columns: 1fr !important;
    }
    .admin-content .admin-market-day-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-market-exception-row {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
      padding: 8px !important;
      border: 1px solid #e5e7eb !important;
      border-radius: 12px !important;
      background: #f8fafc !important;
    }
    .admin-content .admin-market-exception-row > *:nth-child(1) {
      grid-column: 1 / -1 !important;
    }
    .admin-content .admin-market-exception-row > *:nth-child(4) {
      grid-column: 1 / 2 !important;
    }
    .admin-content .admin-market-exception-row > *:nth-child(5) {
      grid-column: 2 / 3 !important;
      justify-self: end !important;
    }
    .admin-content .admin-market-modal-body {
      padding: 14px !important;
      overflow-y: auto !important;
      max-height: calc(100vh - 120px) !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-content .admin-market-modal-overlay {
      align-items: start !important;
      padding-top: 10px !important;
      padding-bottom: 10px !important;
    }
    .admin-content .admin-modal-backdrop {
      display: block !important;
      overflow-y: auto !important;
      padding: 10px !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-content .admin-modal-card {
      width: 100% !important;
      max-width: 100% !important;
      max-height: calc(100vh - 20px) !important;
      border-radius: 16px !important;
      overflow: hidden !important;
    }
    .admin-content .admin-modal-body {
      max-height: calc(100vh - 154px) !important;
      overflow-y: auto !important;
      padding: 12px !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-content .admin-outcome-form {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
      margin-top: 10px !important;
    }
    .admin-content .admin-outcome-form label {
      gap: 4px !important;
      font-size: 10px !important;
    }
    .admin-content .admin-outcome-form input,
    .admin-content .admin-outcome-form select,
    .admin-content .admin-outcome-form button,
    .admin-content .admin-outcome-form div[style*="58px"] {
      min-height: 40px !important;
      height: 40px !important;
      font-size: 12px !important;
      padding-left: 8px !important;
      padding-right: 8px !important;
    }
    .admin-content .admin-outcome-form label:nth-child(2) {
      grid-column: 1 / -1 !important;
    }
    .admin-content .admin-outcome-selected {
      padding: 8px 10px !important;
      margin-top: 10px !important;
      margin-bottom: 4px !important;
      border-radius: 12px !important;
      gap: 8px !important;
    }
    .admin-content .admin-dashboard-kpis {
      display: grid !important;
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-dashboard-kpis article {
      min-height: 84px !important;
      border-radius: 14px !important;
      padding: 9px 7px !important;
    }
    .admin-content .admin-dashboard-kpis article > div:first-child {
      font-size: 8px !important;
      line-height: 1.05 !important;
      gap: 4px !important;
    }
    .admin-content .admin-dashboard-kpis article > strong {
      margin-top: 9px !important;
      font-size: 16px !important;
      line-height: 1 !important;
      white-space: nowrap;
    }
    .admin-content .admin-dashboard-kpis article > div:last-child {
      margin-top: 8px !important;
      font-size: 8px !important;
      gap: 4px !important;
    }
    .admin-content .admin-dashboard-minis {
      display: grid !important;
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-dashboard-minis article {
      min-height: 72px !important;
      padding: 8px 6px !important;
      border-radius: 13px !important;
      gap: 4px !important;
      justify-items: center;
      text-align: center;
    }
    .admin-content .admin-dashboard-minis article strong {
      font-size: 13px !important;
      line-height: 1 !important;
    }
    .admin-content .admin-dashboard-minis article span {
      font-size: 9px !important;
      line-height: 1.1 !important;
    }
    .admin-content .admin-payout-toolbar {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
      align-items: stretch !important;
    }
    .admin-content .admin-payout-search,
    .admin-content .admin-payout-date {
      display: grid !important;
      grid-template-columns: 22px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 8px !important;
      width: 100% !important;
      min-width: 0 !important;
      min-height: 42px !important;
      height: 42px !important;
    }
    .admin-content .admin-payout-search input,
    .admin-content .admin-payout-date input {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 0 !important;
      height: 100% !important;
      padding: 0 !important;
    }
    .admin-content .admin-payout-toolbar > button {
      width: 100% !important;
      justify-content: center !important;
    }
    .admin-content .admin-payout-table-wrap {
      overflow: visible !important;
      border: 0 !important;
      background: transparent !important;
    }
    .admin-content .admin-payout-table {
      display: grid !important;
      min-width: 0 !important;
      background: transparent !important;
      border-spacing: 0 !important;
    }
    .admin-content .admin-payout-table tbody {
      display: grid !important;
      gap: 10px !important;
      width: 100% !important;
    }
    .admin-content .admin-payout-table tr {
      display: grid !important;
      height: auto !important;
      margin: 0 !important;
      padding: 10px !important;
      border: 1px solid #e5e7eb !important;
      border-radius: 14px !important;
      background: #fff !important;
    }
    .admin-content .admin-payout-table td {
      display: grid !important;
      grid-template-columns: 82px minmax(0, 1fr) !important;
      gap: 8px !important;
      padding: 7px 0 !important;
      border-bottom: 1px solid #f1f5f9 !important;
    }
    .admin-content .admin-payout-table td::before {
      content: attr(data-label);
      color: #64748b;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: .03em;
    }
    .admin-content .admin-payout-table td:last-child {
      border-bottom: 0 !important;
    }
    .admin-content .admin-payout-table td:last-child > .admin-payout-actions {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
      width: 100% !important;
      align-items: stretch !important;
    }
    .admin-content .admin-payout-actions .admin-payout-utr {
      grid-column: 1 / -1 !important;
      width: 100% !important;
      height: 42px !important;
      border-radius: 10px !important;
      font-size: 16px !important;
    }
    .admin-content .admin-payout-actions button {
      width: 100% !important;
      justify-content: center !important;
      border-radius: 10px !important;
      white-space: normal !important;
      line-height: 1.1 !important;
      padding: 8px !important;
    }
    .admin-content .admin-payout-table-wrap footer {
      display: none !important;
    }
    @media (max-width: 390px) {
      .admin-content .admin-payout-table td {
        grid-template-columns: 74px minmax(0, 1fr) !important;
      }
      .admin-content .admin-payout-table td:last-child > .admin-payout-actions {
        grid-template-columns: 1fr !important;
      }
    }
  }
  @media (max-width: 520px) {
    .admin-content [style*="repeat(4"],
    .admin-content [style*="repeat(3"],
    .admin-content [style*="repeat(2"],
    .admin-content [style*="1fr 1fr"] {
      grid-template-columns: 1fr !important;
    }
    .admin-content .admin-dashboard-kpis,
    .admin-content .admin-dashboard-minis {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-outcome-form {
      grid-template-columns: 1fr !important;
      gap: 8px !important;
    }
    .admin-content .admin-outcome-form label:nth-child(2),
    .admin-content .admin-outcome-form label:nth-child(3) {
      grid-column: auto !important;
    }
    .admin-content .admin-outcome-form [style*="display: flex"] {
      flex-wrap: nowrap !important;
    }
    .admin-content .admin-outcome-form input,
    .admin-content .admin-outcome-form select,
    .admin-content .admin-outcome-form button {
      width: 100% !important;
      min-width: 0 !important;
    }
  }
  @media (max-width: 390px) {
    .admin-content .admin-payout-table td {
      grid-template-columns: 74px minmax(0, 1fr) !important;
    }
    .admin-content .admin-payout-table td:last-child > .admin-payout-actions {
      grid-template-columns: 1fr !important;
    }
  }
`;

export default AdminLayout;
